import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Trash2, Pencil, Droplets, Sun, Cloud, Thermometer, AlertTriangle, TrendingUp, Loader2, Info, ExternalLink, Tag, Layers, Camera, Archive, ImagePlus, Home, Moon, HelpCircle, Flower2, ChevronDown, ChevronLeft, ChevronRight, Share2, QrCode, Crop, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { MonsteraSpinner } from './MonsteraSpinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

import { getPhoto, savePhoto as saveLocalPhoto, updatePlant as updateLocalPlant, getAllPhotos as getAllLocalPhotos, addPhotoToPlant as addLocalPhoto, deletePhotoByIndex as deleteLocalPhotoByIndex, replacePhotoByIndex as replaceLocalPhotoByIndex, setPhotoAsCover as setLocalPhotoAsCover, reorderPhotos as reorderLocalPhotos, archivePlant as archiveLocalPlant } from '../services/localPlantStore';
import { withRetry } from '../utils/retryUpload';
import { capitalizeTemperatureUnits } from './add-plant-steps/plantUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { PhotoGallery } from './PhotoGallery';
import { ArchiveDialog } from './ArchiveDialog';
import { IconLegend } from './IconLegend';
import { CareLog } from './CareLog';
import { PhotoCropDialog } from './PhotoCropDialog';
import { invalidatePlantCardThumb } from './PlantCard';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

import { API_BASE_URL } from '../utils/apiBase';
const BACKEND_URL = API_BASE_URL;
const MAX_PHOTOS = 5;

const PLANT_SOURCES = [
  { name: 'Missouri Botanical Garden', url: (q) => `https://www.missouribotanicalgarden.org/PlantFinder/PlantFinderProfileResults.aspx?basic=${encodeURIComponent(q)}`, testid: 'source-mobot' },
  { name: 'ASPCA Toxic Plant List', url: () => `https://www.aspca.org/pet-care/animal-poison-control/toxic-and-non-toxic-plants`, testid: 'source-aspca' },
  { name: 'Royal Horticultural Society', url: (q) => `https://www.rhs.org.uk/search?query=${encodeURIComponent(q)}`, testid: 'source-rhs' },
  { name: 'The Spruce', url: (q) => `https://www.thespruce.com/search?q=${encodeURIComponent(q)}`, testid: 'source-spruce' },
  { name: 'Wikipedia', url: (q) => `https://en.wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(q)}`, testid: 'source-wikipedia' },
];

// Threshold above which the value gets a "Read more" / "Show less" toggle.
// Keeps the toxicity row + any other AI-filled long text compact on mobile.
const CARE_ROW_TRUNCATE_LEN = 80;

/**
 * Format a `dormancy_months` array like [10,11,12,1,2] into a compact
 * locale-aware label such as "Oct–Feb" or "Oct, Dec" when the months are
 * not contiguous. Wraps around Dec→Jan for winter dormancy patterns.
 * @param {number[]} months integers 1-12
 * @param {string} language BCP-47 language code (defaults to 'en')
 */
export function formatDormancyMonths(months, language = 'en') {
  if (!Array.isArray(months) || months.length === 0) return '';
  const sorted = [...new Set(months.filter((m) => Number.isInteger(m) && m >= 1 && m <= 12))].sort((a, b) => a - b);
  if (sorted.length === 0) return '';
  const fmt = new Intl.DateTimeFormat(language, { month: 'short' });
  const name = (m) => fmt.format(new Date(2000, m - 1, 1));

  if (sorted.length === 1) return name(sorted[0]);

  // Detect contiguous (with possible wrap): treat as ranges. To handle
  // Dec→Jan wrap, if the set contains both December (12) and January (1)
  // AND is otherwise contiguous across the year boundary, pick the start
  // of the wrap. Otherwise fall back to comma-joined names.
  const isContiguousWithWrap = (arr) => {
    // Try each rotation and see if it forms a strictly increasing run mod 12
    for (let start = 0; start < arr.length; start++) {
      const rotated = [...arr.slice(start), ...arr.slice(0, start)];
      let ok = true;
      for (let i = 1; i < rotated.length; i++) {
        const prev = rotated[i - 1];
        const cur = rotated[i];
        const expected = prev === 12 ? 1 : prev + 1;
        if (cur !== expected) { ok = false; break; }
      }
      if (ok) return { ok: true, first: rotated[0], last: rotated[rotated.length - 1] };
    }
    return { ok: false };
  };
  const { ok, first, last } = isContiguousWithWrap(sorted);
  if (ok) return `${name(first)}–${name(last)}`;
  return sorted.map(name).join(', ');
}

/**
 * Restore missing spaces in AI-generated free-text that got mangled by an
 * older version of the backend sanitizer (which stripped `<br>` tags without
 * inserting a space in their place). Legacy strings like "Nov-Febleaves die
 * back" are transparently rendered as "Nov-Feb leaves die back".
 *
 * Only applied at display time — the underlying stored value stays untouched
 * so a future backfill can rewrite it cleanly.
 */
function unmangleAiText(text) {
  if (typeof text !== 'string' || !text) return text;
  // Restore missing spaces when the legacy backend sanitizer stripped an
  // HTML tag between a month abbrev and the next word ("Nov-Feb<br>leaves"
  // → the AI-facing sanitizer used to remove the tag with no replacement,
  // resulting in "Nov-Febleaves"). Each month abbrev carries a negative
  // lookahead ruling out the letters that would make it a full month name,
  // so legit prose like "in February and March" is never split.
  return text
    .replace(
      /\b(January|February|March|April|June|July|August|September|October|November|December|Jan(?!uary)|Feb(?!ruary)|Mar(?!ch)|Apr(?!il)|May|Jun(?!e)|Jul(?!y)|Aug(?!ust)|Sep(?!tember)|Oct(?!ober)|Nov(?!ember)|Dec(?!ember))([a-z]{2,})/g,
      '$1 $2'
    )
    .replace(/\s{2,}/g, ' ');
}

// Values shorter than this render happily in a single grid column; longer
// values (typically toxicity/environment/dormancy with attached notes)
// auto-promote to span the full row so they don't cramp their neighbour.
const CARE_ROW_COMPACT_MAX = 40;

const CareRow = ({ icon, label, value, fullWidth = false }) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useLanguage();
  const isString = typeof value === 'string';
  const cleaned = isString ? unmangleAiText(value) : value;
  const tooLong = typeof cleaned === 'string' && cleaned.length > CARE_ROW_TRUNCATE_LEN;
  const shown = tooLong && !expanded
    ? cleaned.slice(0, CARE_ROW_TRUNCATE_LEN).trimEnd() + '…'
    : cleaned;
  // Auto-span full row for long or non-string values so short one-liners
  // stay side-by-side and save vertical scroll.
  const spanFull = fullWidth || !isString || (typeof cleaned === 'string' && cleaned.length > CARE_ROW_COMPACT_MAX);
  return (
    <div
      className={`flex gap-3 p-3 rounded-xl ${spanFull ? 'sm:col-span-2' : ''}`}
      style={{ backgroundColor: 'hsl(82, 55%, 98%)', border: '1px solid hsl(143, 28%, 90%)' }}
    >
      <div className="mt-0.5 flex-shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold mb-0.5" style={{ color: 'hsl(143, 28%, 25%)' }}>{label}</p>
        <p className="text-sm" style={{ color: 'hsl(125, 12%, 25%)' }}>{shown}</p>
        {tooLong && (
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="text-xs font-medium mt-1 underline-offset-2 hover:underline"
            style={{ color: 'hsl(143, 28%, 35%)' }}
            data-testid={`care-row-toggle-${label}`}
            aria-expanded={expanded}
          >
            {expanded ? (t('showLess') || 'Show less') : (t('readMore') || 'Read more')}
          </button>
        )}
      </div>
    </div>
  );
};

export const PlantDetailModal = ({ plant, onClose, onEdit, onDelete, onPlantAdded, isLocalMode = false, enlargedPhoto: enlargedPhotoProp, onEnlargedPhotoChange, onShareThisPlant, onShowQr }) => {
  const { language, t } = useLanguage();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  // Enlarged photo state is owned by parent (LibraryPage) so its back-button
  // handler can close just the enlarged view first instead of dismissing the
  // whole detail modal. Falls back to local state if not provided.
  const [localEnlargedPhoto, setLocalEnlargedPhoto] = useState(null);
  const enlargedPhoto = onEnlargedPhotoChange ? enlargedPhotoProp : localEnlargedPhoto;
  const setEnlargedPhoto = onEnlargedPhotoChange || setLocalEnlargedPhoto;
  // Fullscreen viewer navigation (prev/next/swipe/keyboard). Pass a concrete
  // index (not a functional updater) so it works whether the setter is our
  // local useState or the parent-owned one.
  const enlargedTouchRef = useRef({ startX: 0, startY: 0, moved: false });
  // Pinch-to-zoom + pan state for the fullscreen viewer.
  const [zoom, setZoom] = useState({ scale: 1, x: 0, y: 0 });
  const MAX_ZOOM = 4;
  const gestureRef = useRef({ mode: null, startDist: 0, startScale: 1, startX: 0, startY: 0, startTx: 0, startTy: 0, moved: false, lastTap: 0, pointerDown: false });
  const clampPan = (scale, x, y) => {
    if (scale <= 1) return { x: 0, y: 0 };
    const maxX = (scale - 1) * (typeof window !== 'undefined' ? window.innerWidth : 400) / 2;
    const maxY = (scale - 1) * (typeof window !== 'undefined' ? window.innerHeight : 800) / 2;
    return { x: Math.max(-maxX, Math.min(maxX, x)), y: Math.max(-maxY, Math.min(maxY, y)) };
  };
  const resetZoom = () => setZoom({ scale: 1, x: 0, y: 0 });
  const toggleZoom = () => setZoom(z => (z.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2.5, x: 0, y: 0 }));
  // Single-vs-double disambiguation: a plain tap/click closes the viewer, but
  // we delay it briefly so a double-tap/click (zoom) can cancel the close.
  const closeTimerRef = useRef(null);
  const cancelClose = () => { if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; } };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => { setEnlargedPhoto(null); }, 260);
  };
  const showPrevEnlarged = () => {
    if (enlargedPhoto === null || photos.length < 2) return;
    cancelClose();
    setEnlargedPhoto((enlargedPhoto - 1 + photos.length) % photos.length);
  };
  const showNextEnlarged = () => {
    if (enlargedPhoto === null || photos.length < 2) return;
    cancelClose();
    setEnlargedPhoto((enlargedPhoto + 1) % photos.length);
  };
  const [translatedCare, setTranslatedCare] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [translationFailed, setTranslationFailed] = useState(false);
  const translationCache = useRef({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // Holds a photo whose add/merge upload failed with a TRANSIENT error
  // (network / Drive 5xx / rate-limit) so the user can retry instead of
  // losing it. Permanent rejections (dup photo / 5-photo cap) don't set this.
  const [failedPhotoAdd, setFailedPhotoAdd] = useState(null);
  // Shows "Retrying… (n/3)" while withRetry backs off on transient errors.
  const [retryStatus, setRetryStatus] = useState(null);
  // Local mirror of the plant's care log so add/undo actions repaint the
  // timeline instantly without waiting for the parent to reload the plant.
  const [careLog, setCareLog] = useState(() => plant.care_log || []);
  // Icon-legend popover container ref — receives keyboard focus on open
  // so users can Tab into and scroll the legend without the search input
  // autofocusing and popping the iPad keyboard.
  const iconLegendPopoverRef = useRef(null);
  const photoInputRef = useRef(null);
  const photoCameraInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const replaceCameraInputRef = useRef(null);
  // Null | 'add' (controls a small chooser dialog: Camera vs Gallery)
  const [sourcePickerFor, setSourcePickerFor] = useState(null);

  const handleSourceChoice = (source) => {
    setSourcePickerFor(null);
    // Defer the click to next tick so the chooser dialog has fully unmounted
    // before the file picker / camera intent fires. Otherwise Radix's pointer
    // event blocker can swallow the synthetic click on iOS Safari.
    setTimeout(() => {
      if (source === 'camera') {
        photoCameraInputRef.current?.click();
      } else {
        photoInputRef.current?.click();
      }
    }, 50);
  };
  const replaceIndexRef = useRef(null);

  // First-time "scroll for care details" peek hint (mobile only).
  // Guides the user to the fold below the photo/name section so they
  // discover the care details grid without needing to intuit it. Shown
  // once per browser (localStorage flag), auto-dismissed on any scroll
  // of the content area, close, or after 6s.
  const scrollAreaRef = useRef(null);
  const [showPeekHint, setShowPeekHint] = useState(false);

  const photoCount = photos.length;
  const canAddPhoto = photoCount < MAX_PHOTOS;

  useEffect(() => {
    loadAllPhotos();
  }, [plant.plant_id, plant.photo_drive_ids, plant.photo_drive_id, isLocalMode]);

  // Keyboard nav for the fullscreen viewer.
  useEffect(() => {
    if (enlargedPhoto === null || photos.length < 2) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); showPrevEnlarged(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); showNextEnlarged(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enlargedPhoto, photos.length]);

  // Reset zoom whenever the viewed photo changes or the viewer closes.
  useEffect(() => { resetZoom(); cancelClose(); return () => cancelClose(); }, [enlargedPhoto]);

  const loadAllPhotos = async (overrideDriveIds) => {
    setLoading(true);
    try {
      if (isLocalMode) {
        const localPhotos = await getAllLocalPhotos(plant.plant_id);
        if (localPhotos.length > 0) {
          setPhotos(localPhotos.map(p => ({
            // `photoIndex` is the STORED IndexedDB index used to derive
            // the record key (e.g. plantId_2). We keep it alongside the
            // array position because deletes and adds can leave gaps —
            // the array position drifts, the stored index does not.
            // Downstream delete/replace/set-cover handlers use this
            // instead of the array position to avoid silent no-op
            // operations against a stale key.
            photoIndex: p.index,
            src: `data:image/jpeg;base64,${p.image_base64}`,
            thumbnail: p.thumbnail_base64 ? `data:image/jpeg;base64,${p.thumbnail_base64}` : null,
          })));
        } else {
          // Backward compat: try old single-photo key
          const base64 = await getPhoto(plant.plant_id);
          if (base64) {
            setPhotos([{ src: `data:image/jpeg;base64,${base64}`, thumbnail: null }]);
          } else {
            setPhotos([]);
          }
        }
      } else {
        const driveIds = overrideDriveIds || plant.photo_drive_ids || (plant.photo_drive_id ? [plant.photo_drive_id] : []);
        if (driveIds.length === 0) { setPhotos([]); return; }
        const sessionToken = localStorage.getItem('auth_hint');
        const loaded = await Promise.all(driveIds.map(async (id) => {
          try {
            const resp = await axios.get(`${BACKEND_URL}/api/photos/${id}`, {
              headers: { Authorization: `Bearer ${sessionToken}` },
              responseType: 'blob',
            });
            return { src: URL.createObjectURL(resp.data), thumbnail: null, driveId: id };
          } catch {
            return null;
          }
        }));
        setPhotos(loaded.filter(Boolean));
      }
    } catch (err) {
      console.error('Failed to load photos:', err);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  // Translate care details when language is not English
  const translateCare = useCallback(async () => {
    if (language === 'en' || !plant.care_details) {
      setTranslatedCare(null);
      setTranslationFailed(false);
      return;
    }
    const cacheKey = `${plant.plant_id}_${language}`;
    if (translationCache.current[cacheKey]) {
      setTranslatedCare(translationCache.current[cacheKey]);
      setTranslationFailed(false);
      return;
    }
    setTranslating(true);
    setTranslationFailed(false);
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/translate-care-details`, {
        care_details: plant.care_details,
        target_language: language,
      });
      translationCache.current[cacheKey] = resp.data;
      setTranslatedCare(resp.data);
    } catch (err) {
      console.error('Translation failed:', err);
      setTranslationFailed(true);
    } finally {
      setTranslating(false);
    }
  }, [plant.plant_id, plant.care_details, language]);

  useEffect(() => {
    translateCare();
  }, [translateCare]);

  // First-time peek hint: after photos finish loading, check if we
  // should nudge the user to scroll for care details. Runs on the
  // first render where `loading` flips to false. Guards: mobile only
  // (< 640px), never shown before (localStorage), and only when the
  // scroll area actually overflows (no hint if everything fits).
  useEffect(() => {
    if (loading) return;
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 640) return;
    try {
      if (localStorage.getItem('plant_modal_peek_hint_seen') === '1') return;
    } catch (_) { /* SSR / privacy mode */ }

    // Wait one frame for the scroll area to lay out, then check overflow.
    const timer = setTimeout(() => {
      const el = scrollAreaRef.current;
      if (!el) return;
      if (el.scrollHeight <= el.clientHeight + 8) return; // nothing to scroll to
      setShowPeekHint(true);
    }, 350);
    return () => clearTimeout(timer);
  }, [loading]);

  // Persist the "seen" flag and hide the hint. Called on scroll, close,
  // or the 6-second auto-timeout.
  const dismissPeekHint = useCallback(() => {
    setShowPeekHint(false);
    try { localStorage.setItem('plant_modal_peek_hint_seen', '1'); } catch (_) { /* noop */ }
  }, []);

  // Auto-hide the hint after 6s if the user hasn't scrolled or closed.
  useEffect(() => {
    if (!showPeekHint) return;
    const timer = setTimeout(dismissPeekHint, 6000);
    return () => clearTimeout(timer);
  }, [showPeekHint, dismissPeekHint]);

  const care = translatedCare || plant.care_details || {};

  const handleDelete = () => {
    onDelete(plant.plant_id);
    setShowDeleteAlert(false);
  };

  const handleArchive = async (reason) => {
    try {
      let queued = false;
      if (isLocalMode) {
        await archiveLocalPlant(plant.plant_id, reason);
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        const resp = await axios.post(`${BACKEND_URL}/api/plants/${plant.plant_id}/archive`, { reason }, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        queued = !!resp?.data?.queued;
      }
      if (!queued) toast.success('Plant archived');
      setShowArchiveDialog(false);
      onPlantAdded();
      onClose();
    } catch (error) {
      console.error('Failed to archive plant:', error);
      toast.error('Failed to archive plant');
    }
  };

  // Crop-and-rotate before upload — same UX as the initial
  // identification's CropStep. `pendingPhotoMode` is either 'add' or
  // { mode: 'replace', index } so a single dialog powers both flows.
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const [pendingPhotoMode, setPendingPhotoMode] = useState(null);
  // Bulk add: queue of remaining files to crop+upload one at a time, plus the
  // total so the crop dialog can show a "Photo X of Y" heading.
  const [addPhotoQueue, setAddPhotoQueue] = useState([]);
  const [addPhotoTotal, setAddPhotoTotal] = useState(0);

  const handlePhotoSelected = async (e) => {
    const raw = Array.from(e.target.files || []);
    if (raw.length === 0) return;
    if (!canAddPhoto) {
      toast.error(`Maximum ${MAX_PHOTOS} photos per plant`);
      if (photoInputRef.current) photoInputRef.current.value = '';
      if (photoCameraInputRef.current) photoCameraInputRef.current.value = '';
      return;
    }
    const { normalizeImageFile, isHeic } = await import('../utils/imageFormat');
    // Only take as many as remaining slots allow; warn if we had to drop some.
    const remainingSlots = MAX_PHOTOS - photoCount;
    let picked = raw;
    if (raw.length > remainingSlots) {
      picked = raw.slice(0, remainingSlots);
      toast.info((t('bulkPhotoCapped') || 'Only {n} more photo(s) fit — the rest were skipped.').replace('{n}', remainingSlots));
    }
    const anyHeic = picked.some(isHeic);
    if (anyHeic) toast.info(t('convertingHeic') || 'Converting HEIC…');
    const normalized = [];
    for (const f of picked) normalized.push(isHeic(f) ? await normalizeImageFile(f) : f);
    const [first, ...rest] = normalized;
    setAddPhotoTotal(normalized.length);
    setAddPhotoQueue(rest);
    setPendingPhotoFile(first);
    setPendingPhotoMode({ mode: 'add' });
  };

  // Load the next queued photo into the crop dialog (called after each upload).
  const advanceAddPhotoQueue = () => {
    setAddPhotoQueue(prev => {
      if (prev.length === 0) { setAddPhotoTotal(0); return prev; }
      const [next, ...rest] = prev;
      setPendingPhotoFile(next);
      setPendingPhotoMode({ mode: 'add' });
      return rest;
    });
  };

  const doAddPhotoUpload = async (base64) => {
    setUploadingPhoto(true);
    setFailedPhotoAdd(null);
    setRetryStatus(null);
    try {
      let overrideDriveIds = null;
      if (isLocalMode) {
        await addLocalPhoto(plant.plant_id, base64);
        await updateLocalPlant(plant.plant_id, { has_photo: true });
      } else {
        const resp = await withRetry(async () => {
          const sessionToken = localStorage.getItem('auth_hint');
          return axios.post(`${BACKEND_URL}/api/plants/${plant.plant_id}/photos`, {
            image_base64: base64,
          }, { headers: { Authorization: `Bearer ${sessionToken}` } });
        }, { onAttempt: (n, total) => setRetryStatus(`${n}/${total}`) });
        overrideDriveIds = resp?.data?.photo_drive_ids || null;
      }
      toast.success(t('photoAdded') || 'Photo added');
      invalidatePlantCardThumb(plant.plant_id);
      if (plant.photo_drive_id) invalidatePlantCardThumb(plant.photo_drive_id);
      onPlantAdded();
      await loadAllPhotos(overrideDriveIds);
    } catch (error) {
      // Offline: the axios interceptor queued it and already toasted — not a
      // failure, so don't offer manual retry.
      if (!error?.__queued) {
        const status = error?.response?.status;
        const msg = error?.response?.data?.detail || 'Failed to add photo';
        console.error('Failed to add photo:', error);
        toast.error(msg);
        // Permanent rejections (400 max, 409 dup) can't be retried; transient
        // errors (survived auto-retry) keep the photo for a manual retry.
        if (status !== 400 && status !== 409) setFailedPhotoAdd(base64);
      }
    } finally {
      setUploadingPhoto(false);
      setRetryStatus(null);
      if (photoInputRef.current) photoInputRef.current.value = '';
      if (photoCameraInputRef.current) photoCameraInputRef.current.value = '';
      // Bulk add: move on to the next queued photo (no-op for single adds).
      advanceAddPhotoQueue();
    }
  };

  const handleDeletePhoto = async (photoIndex) => {
    if (photos.length === 0) return;
    try {
      if (isLocalMode) {
        // Use the STORED IndexedDB index (photos[i].photoIndex),
        // NOT the array position — deleting a middle photo leaves a
        // gap between stored indices while the array closes up, so
        // subsequent array positions no longer match stored keys.
        const storedIndex = photos[photoIndex]?.photoIndex ?? photoIndex;
        await deleteLocalPhotoByIndex(plant.plant_id, storedIndex);
        if (photos.length <= 1) {
          await updateLocalPlant(plant.plant_id, { has_photo: false });
        }
        // Reload from IDB so state exactly reflects storage — no
        // optimistic filter, no drift between modal sessions.
        await loadAllPhotos();
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        await axios.delete(`${BACKEND_URL}/api/plants/${plant.plant_id}/photos/${photoIndex}`, {
          headers: { Authorization: `Bearer ${sessionToken}` },
        });
        // Drive mode: server has re-numbered the photos, filter locally.
        setPhotos(prev => prev.filter((_, i) => i !== photoIndex));
      }
      toast.success('Photo deleted');
      // The library grid card caches thumbnails per plant_id/drive_id
      // in a module-level Map — drop the cover-key's entry so the card
      // re-reads from IDB / Drive on next render. Otherwise the card
      // keeps showing the just-deleted image until a page reload.
      invalidatePlantCardThumb(plant.plant_id);
      if (plant.photo_drive_id) invalidatePlantCardThumb(plant.photo_drive_id);
      onPlantAdded();
    } catch (error) {
      console.error('Failed to delete photo:', error);
      toast.error('Failed to delete photo');
    }
  };

  const handleReplacePhotoClick = (photoIndex) => {
    replaceIndexRef.current = photoIndex;
    replaceInputRef.current?.click();
  };

  const handleReplacePhotoSelected = async (e) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    const photoIndex = replaceIndexRef.current;
    if (photoIndex == null) return;
    const { normalizeImageFile, isHeic } = await import('../utils/imageFormat');
    let file = raw;
    if (isHeic(raw)) {
      toast.info(t('convertingHeic') || 'Converting HEIC…');
      file = await normalizeImageFile(raw);
    }
    setPendingPhotoFile(file);
    setPendingPhotoMode({ mode: 'replace', index: photoIndex });
  };

  const doReplacePhotoUpload = async (base64, dataUrl, photoIndex) => {
    setUploadingPhoto(true);
    try {
      if (isLocalMode) {
        // Same drift guard as delete — use the stored IDB index.
        const storedIndex = photos[photoIndex]?.photoIndex ?? photoIndex;
        await replaceLocalPhotoByIndex(plant.plant_id, storedIndex, base64);
        // Reload from IDB to stay in lockstep with storage — same
        // reasoning as handleDeletePhoto.
        await loadAllPhotos();
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        await axios.put(`${BACKEND_URL}/api/plants/${plant.plant_id}/photos/${photoIndex}`, {
          image_base64: base64,
        }, { headers: { Authorization: `Bearer ${sessionToken}` } });
        // Drive mode: patch the local carousel with the new dataUrl.
        setPhotos(prev => prev.map((p, i) => i === photoIndex ? { ...p, src: dataUrl, thumbnail: null } : p));
      }
      toast.success(t('photoReplaced') || 'Photo replaced');
      invalidatePlantCardThumb(plant.plant_id);
      if (plant.photo_drive_id) invalidatePlantCardThumb(plant.photo_drive_id);
      onPlantAdded();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to replace photo';
      console.error('Failed to replace photo:', error);
      toast.error(msg);
    } finally {
      setUploadingPhoto(false);
      if (replaceInputRef.current) replaceInputRef.current.value = '';
      if (replaceCameraInputRef.current) replaceCameraInputRef.current.value = '';
    }
  };

  const handleSetCover = async (photoIndex) => {
    if (photoIndex === 0 || photoIndex == null) return;
    setUploadingPhoto(true);
    try {
      if (isLocalMode) {
        // Same drift guard as delete — use the stored IDB index.
        const storedIndex = photos[photoIndex]?.photoIndex ?? photoIndex;
        await setLocalPhotoAsCover(plant.plant_id, storedIndex);
        // Reload from IDB — set-cover may either SWAP records or
        // RE-KEY them (depending on whether a record at index 0
        // exists), so a slot-preserving optimistic swap can't reliably
        // keep our photoIndex bindings in sync.
        await loadAllPhotos();
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        await axios.post(
          `${BACKEND_URL}/api/plants/${plant.plant_id}/photos/${photoIndex}/make-cover`,
          {},
          { headers: { Authorization: `Bearer ${sessionToken}` } },
        );
        // Drive mode: optimistic swap — the server reorders
        // photo_drive_ids symmetrically, so a simple slot swap of
        // src/thumbnail/driveId reflects storage exactly. photoIndex
        // is only used in local mode.
        setPhotos((prev) => {
          if (photoIndex >= prev.length) return prev;
          const next = prev.slice();
          const a = next[0];
          const b = next[photoIndex];
          next[0] = { ...a, src: b.src, thumbnail: b.thumbnail, driveId: b.driveId };
          next[photoIndex] = { ...b, src: a.src, thumbnail: a.thumbnail, driveId: a.driveId };
          return next;
        });
      }
      toast.success('Cover photo updated');
      invalidatePlantCardThumb(plant.plant_id);
      if (plant.photo_drive_id) invalidatePlantCardThumb(plant.photo_drive_id);
      onPlantAdded();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to set cover photo';
      console.error('Failed to set cover photo:', error);
      toast.error(msg);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Drag-to-reorder: move photo from array index `from` to `to`. The new
  // position 0 becomes the cover. Optimistic, then persisted.
  const handleReorder = async (from, to) => {
    if (from === to || from == null || to == null) return;
    const reordered = photos.slice();
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    const prevPhotos = photos;
    try {
      if (isLocalMode) {
        // Persist using the OLD stored indexes in their new order, then
        // remap the in-memory photoIndex to the new positions (localStore
        // renumbers records 0..n) WITHOUT calling loadAllPhotos — a reload
        // flips `loading` true, which UNMOUNTS PhotoGallery (spinner) and
        // steals keyboard focus mid-reorder, breaking arrow-key chaining.
        const orderedOld = reordered.map((p) => p.photoIndex ?? 0);
        setPhotos(reordered.map((p, idx) => ({ ...p, photoIndex: idx })));
        await reorderLocalPhotos(plant.plant_id, orderedOld);
      } else {
        setPhotos(reordered);
        const sessionToken = localStorage.getItem('auth_hint');
        await axios.put(
          `${BACKEND_URL}/api/plants/${plant.plant_id}/photos/reorder`,
          { order: reordered.map((p) => p.driveId) },
          { headers: { Authorization: `Bearer ${sessionToken}` } },
        );
      }
      toast.success(t('photosReordered') || 'Photo order updated');
      invalidatePlantCardThumb(plant.plant_id);
      if (plant.photo_drive_id) invalidatePlantCardThumb(plant.photo_drive_id);
      onPlantAdded();
    } catch (error) {
      setPhotos(prevPhotos);
      toast.error(error?.response?.data?.detail || t('photosReorderFailed') || 'Failed to reorder photos');
    }
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent
          hideCloseButton
          className="max-w-2xl p-0 max-h-[90vh] overflow-hidden flex flex-col"
          data-testid="plant-detail-modal"
          aria-describedby={undefined}
          onPointerDownOutside={(e) => {
            // The enlarged-photo overlay renders as a sibling of this Dialog
            // (not inside the Radix portal). Without this guard, tapping the
            // enlarged backdrop is detected as an "outside click" and Radix
            // dismisses the parent detail dialog — so closing the photo would
            // also close the card and drop the user back at the library.
            if (enlargedPhoto !== null) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (enlargedPhoto !== null) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (enlargedPhoto !== null) {
              e.preventDefault();
              setEnlargedPhoto(null);
            }
          }}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{plant.common_name || t('unnamedPlant') || 'Unnamed plant'}</DialogTitle>
          </DialogHeader>

          {/* Custom close button — always visible over photo */}
          <button
            onClick={onClose}
            className="absolute right-3 top-3 z-10 w-9 h-9 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
            style={{ backgroundColor: 'rgba(255,255,255,0.92)', border: '1px solid rgba(0,0,0,0.08)' }}
            data-testid="plant-detail-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" style={{ color: 'hsl(143, 28%, 25%)' }} />
          </button>

          {/* Photo Gallery */}
          {loading ? (
            <div className="flex items-center justify-center flex-shrink-0" style={{ height: '14rem', backgroundColor: 'hsl(82, 55%, 95%)' }}>
              <MonsteraSpinner className="w-14 h-14" />
            </div>
          ) : photos.length > 0 ? (
            <div className="relative flex-shrink-0 px-4 pt-4" data-testid="plant-photo">
              <PhotoGallery
                photos={photos}
                onDeletePhoto={handleDeletePhoto}
                onReplacePhoto={handleReplacePhotoClick}
                onSetCover={handleSetCover}
                onReorder={handleReorder}
                onPhotoTap={(index) => setEnlargedPhoto(index)}
                isLocal={isLocalMode}
                extraAction={canAddPhoto ? (
                  <button
                    onClick={() => setSourcePickerFor('add')}
                    disabled={uploadingPhoto}
                    className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity disabled:opacity-50 px-1 py-1 min-h-[32px]"
                    style={{ color: 'hsl(143, 28%, 35%)' }}
                    data-testid="add-another-photo-btn"
                    aria-label={`Add photo (${photoCount} of ${MAX_PHOTOS})`}
                  >
                    {uploadingPhoto ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Camera size={14} aria-hidden="true" />}
                    <span className="hidden sm:inline">{t('addPhoto') || 'Add'}{photoCount > 1 ? ` (${photoCount}/${MAX_PHOTOS})` : ''}</span>
                    <span className="sm:hidden">{t('addPhoto') || 'Add'}</span>
                  </button>
                ) : null}
              />
            </div>
          ) : (
            <div
              className="flex flex-col items-center justify-center flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              style={{ height: '10rem', backgroundColor: 'hsl(82, 55%, 95%)' }}
              onClick={() => !uploadingPhoto && setSourcePickerFor('add')}
              data-testid="add-photo-area"
            >
              {uploadingPhoto ? (
                <MonsteraSpinner className="w-14 h-14" />
              ) : (
                <>
                  <Camera className="w-10 h-10 mb-2" style={{ color: 'hsl(143, 28%, 45%)', opacity: 0.6 }} />
                  <p className="text-sm font-medium" style={{ color: 'hsl(143, 28%, 35%)' }}>{t('addPhoto')}</p>
                </>
              )}
            </div>
          )}
          <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={handlePhotoSelected} className="hidden" />
          <input ref={photoCameraInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoSelected} className="hidden" />
          <input ref={replaceInputRef} type="file" accept="image/*" onChange={handleReplacePhotoSelected} className="hidden" data-testid="replace-photo-input" />
          <input ref={replaceCameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleReplacePhotoSelected} className="hidden" data-testid="replace-photo-camera-input" />

          {retryStatus && (
            <div
              className="mx-6 mt-3 rounded-lg px-3 py-2.5 flex items-center gap-2 text-sm"
              style={{ backgroundColor: 'hsl(143, 30%, 95%)', border: '1px solid hsl(143, 28%, 82%)', color: 'hsl(143, 28%, 25%)' }}
              role="status"
              data-testid="photo-add-retrying"
            >
              <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" aria-hidden="true" />
              <span>{(t('photoRetrying') || 'Retrying… ({n})').replace('{n}', retryStatus)}</span>
            </div>
          )}
          {failedPhotoAdd && (
            <div
              className="mx-6 mt-3 rounded-lg px-3 py-2.5 flex items-center gap-2 text-sm"
              style={{ backgroundColor: 'hsl(40, 90%, 95%)', border: '1px solid hsl(40, 70%, 80%)', color: 'hsl(35, 45%, 28%)' }}
              role="alert"
              data-testid="photo-add-retry-banner"
            >
              <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: 'hsl(35, 80%, 45%)' }} aria-hidden="true" />
              <span className="flex-1 min-w-0">{t('photoAddFailedRetry') || "Couldn't add that photo. Please try again."}</span>
              <button
                type="button"
                onClick={() => doAddPhotoUpload(failedPhotoAdd)}
                disabled={uploadingPhoto}
                className="flex-shrink-0 inline-flex items-center gap-1 font-semibold px-2.5 py-1 rounded-full disabled:opacity-50"
                style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
                data-testid="photo-add-retry-button"
              >
                {uploadingPhoto ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {t('retry') || 'Retry'}
              </button>
              <button
                type="button"
                onClick={() => setFailedPhotoAdd(null)}
                className="flex-shrink-0 p-1 rounded-md hover:bg-black/5"
                aria-label={t('dismiss') || 'Dismiss'}
                data-testid="photo-add-retry-dismiss"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          )}

          {/* Fixed name section - always visible, never scrolls */}
          <div className="px-6 pt-4 pb-2 flex-shrink-0" style={{ borderBottom: '1px solid hsl(143, 28%, 90%)' }}>
            <h2 className="text-xl sm:text-2xl font-display font-bold mb-0.5" style={{ color: 'hsl(143, 28%, 25%)' }} data-testid="plant-detail-name">
              {plant.common_name}
            </h2>
            {plant.scientific_name && (
              <p className="text-sm sm:text-base font-mono" style={{ color: 'hsl(125, 12%, 32%)' }} data-testid="plant-detail-scientific-name">
                {plant.scientific_name}
              </p>
            )}
          </div>

          {/* Scrollable content area - care details only.
              Explicit `touch-action`, `overscroll-behavior`, and the
              legacy `-webkit-overflow-scrolling` property so touch
              scrolling stays smooth on iOS Safari inside a Radix Dialog.
              Without these the flex-column layout can inherit a
              `touch-action: none` from the Dialog overlay and the
              user sees the care icons "stuck" — even though the
              container is scrollable programmatically. */}
          <div
            ref={scrollAreaRef}
            className="overflow-y-auto flex-1 px-6 pb-6 pt-3 relative"
            style={{
              minHeight: 0,
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              touchAction: 'pan-y',
            }}
            data-testid="plant-detail-scroll-area"
            onScroll={(e) => {
              if (showPeekHint && e.currentTarget.scrollTop > 20) dismissPeekHint();
            }}
          >
            {/* Care Details */}
            {plant.care_details && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold" style={{ color: 'hsl(143, 28%, 25%)' }}>
                    {t('careDetails')}
                  </h3>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-100 transition-colors"
                        style={{ color: 'hsl(143, 28%, 45%)' }}
                        aria-label={t('iconLegend') || 'What do these icons mean?'}
                        data-testid="care-icon-legend-trigger"
                      >
                        <HelpCircle className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      ref={iconLegendPopoverRef}
                      tabIndex={-1}
                      side="bottom"
                      align="start"
                      className="w-80 max-w-[calc(100vw-2rem)] max-h-[60vh] overflow-y-auto overscroll-contain p-4 focus:outline-none"
                      // Prevent Radix from auto-focusing the search input
                      // inside IconLegend when the popover opens. On iPad
                      // that autofocus was popping the on-screen keyboard,
                      // shrinking the viewport, and making the legend
                      // appear frozen / unscrollable — even though the user
                      // just wanted to skim the icon meanings.
                      //
                      // We retarget focus to the container itself (tabIndex=-1)
                      // instead of the input — that keeps the on-screen
                      // keyboard closed AND lets keyboard users Tab into
                      // the content, scroll it, and reach the search
                      // field. Focus-scope stays inside the popover so
                      // Radix's Escape-to-close + click-outside dismissal
                      // still work.
                      onOpenAutoFocus={(e) => {
                        e.preventDefault();
                        requestAnimationFrame(() => {
                          iconLegendPopoverRef.current?.focus();
                        });
                      }}
                      // Also block the closing-focus jump (a related iOS
                      // quirk where the parent modal briefly re-focuses a
                      // hidden input and re-triggers the keyboard).
                      onCloseAutoFocus={(e) => e.preventDefault()}
                      collisionPadding={12}
                      data-testid="care-icon-legend-popover"
                    >
                      <IconLegend />
                    </PopoverContent>
                  </Popover>
                  {translating && (
                    <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(143, 28%, 35%)' }} data-testid="translating-indicator">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {t('translating') || 'Translating...'}
                    </span>
                  )}
                  {translationFailed && language !== 'en' && (
                    <button
                      onClick={translateCare}
                      className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
                      style={{ backgroundColor: 'hsl(40, 60%, 95%)', color: 'hsl(40, 50%, 35%)', border: '1px solid hsl(40, 40%, 85%)' }}
                      data-testid="retry-translation-button"
                    >
                      {t('retryTranslation') || 'Retry translation'}
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="care-details-grid">
                {care.family && (
                  <CareRow icon={<Tag className="w-5 h-5" style={{ color: 'hsl(21, 70%, 57%)' }} />} label={t('family')} value={care.family} />
                )}
                {care.plant_type && (
                  <CareRow icon={<Layers className="w-5 h-5" style={{ color: 'hsl(21, 70%, 57%)' }} />} label={t('plantType')} value={care.plant_type} />
                )}
                {care.watering && (
                  <CareRow icon={<Droplets className="w-5 h-5" style={{ color: 'hsl(21, 70%, 57%)' }} />} label={t('watering')} value={care.watering} />
                )}
                {care.light && (
                  <CareRow icon={<Sun className="w-5 h-5" style={{ color: 'hsl(21, 70%, 57%)' }} />} label={t('light')} value={care.light} />
                )}
                {care.humidity && (
                  <CareRow icon={<Cloud className="w-5 h-5" style={{ color: 'hsl(21, 70%, 57%)' }} />} label={t('humidity')} value={care.humidity} />
                )}
                {care.temperature && (
                  <CareRow icon={<Thermometer className="w-5 h-5" style={{ color: 'hsl(21, 70%, 57%)' }} />} label={t('temperature')} value={capitalizeTemperatureUnits(care.temperature)} />
                )}
                {care.toxicity && (
                  <CareRow
                    icon={<AlertTriangle className="w-5 h-5" style={{ color: 'hsl(21, 70%, 57%)' }} />}
                    label={t('toxicity')}
                    value={care.toxicity_notes ? `${care.toxicity} — ${care.toxicity_notes}` : care.toxicity}
                  />
                )}
                {care.environment && (
                  <CareRow
                    icon={<Home className="w-5 h-5" style={{ color: 'hsl(143, 28%, 45%)' }} />}
                    label={t('environment') || 'Environment'}
                    value={care.environment}
                  />
                )}
                {care.growth_rate && (
                  <CareRow icon={<TrendingUp className="w-5 h-5" style={{ color: 'hsl(21, 70%, 57%)' }} />} label={t('growthRate')} value={care.growth_rate} />
                )}
                {/* Blooming — collapses the four bloom_* fields into one row.
                    The 'blooms_indoors: No' case gets its own short "Does not
                    bloom indoors" line so users don't wonder why season/tips
                    are blank. Long combined values auto-span two columns via
                    the CARE_ROW_COMPACT_MAX heuristic inside CareRow. */}
                {(() => {
                  const hasAny =
                    care.bloom_season ||
                    care.bloom_duration ||
                    care.bloom_tips ||
                    care.blooms_indoors;
                  if (!hasAny) return null;
                  let value;
                  if (care.blooms_indoors === 'No' && !care.bloom_season && !care.bloom_tips) {
                    value = t('bloomNotIndoors') || 'Does not bloom indoors';
                  } else {
                    const parts = [];
                    if (care.bloom_season) parts.push(care.bloom_season);
                    if (care.bloom_duration) parts.push(`${t('bloomLasts') || 'Lasts'} ${care.bloom_duration}`);
                    if (care.blooms_indoors === 'Rarely') parts.push(t('bloomRarelyIndoors') || 'Rarely blooms indoors');
                    if (care.blooms_indoors === 'No') parts.push(t('bloomNotIndoors') || 'Does not bloom indoors');
                    const headline = parts.join(' · ');
                    value = care.bloom_tips ? `${headline}${headline ? ' — ' : ''}${care.bloom_tips}` : headline;
                  }
                  return (
                    <CareRow
                      icon={<Flower2 className="w-5 h-5" style={{ color: 'hsl(320, 55%, 55%)' }} />}
                      label={t('blooming') || 'Blooming'}
                      value={value}
                    />
                  );
                })()}
                {care.dormancy_notes && (
                  <CareRow
                    icon={<Moon className="w-5 h-5" style={{ color: 'hsl(230, 40%, 55%)' }} />}
                    label={t('dormancy') || 'Dormancy'}
                    value={
                      Array.isArray(care.dormancy_months) && care.dormancy_months.length > 0
                        ? `${formatDormancyMonths(care.dormancy_months, language)} — ${care.dormancy_notes}`
                        : care.dormancy_notes
                    }
                  />
                )}
                </div>

                {/* AI Disclaimer */}
                <div className="flex items-start gap-2 rounded-lg px-3 py-2 mt-2" style={{ backgroundColor: 'hsl(40, 60%, 96%)', border: '1px solid hsl(40, 40%, 88%)' }} data-testid="ai-care-disclaimer">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'hsl(40, 50%, 45%)' }} />
                  <p className="text-xs leading-relaxed" style={{ color: 'hsl(40, 30%, 30%)' }}>
                    {t('aiDisclaimerDetail')}
                  </p>
                </div>
              </div>
            )}

            {/* Reputable Sources */}
            {(plant.common_name || plant.scientific_name) && (
              <div className="mt-5" data-testid="plant-sources-section">
                <h3 className="text-sm font-semibold mb-2" style={{ color: 'hsl(143, 28%, 25%)' }}>
                  {t('learnMore')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {PLANT_SOURCES.map(src => {
                    const query = plant.scientific_name || plant.common_name;
                    return (
                      <a
                        key={src.testid}
                        href={src.url(query)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all hover:shadow-md"
                        style={{ backgroundColor: 'hsl(143, 28%, 94%)', color: 'hsl(143, 28%, 25%)', border: '1px solid hsl(143, 28%, 85%)' }}
                        data-testid={src.testid}
                      >
                        <ExternalLink className="w-3 h-3" />
                        {src.name}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Care Log — quick-tick + timeline for water/fertilize/repot/prune */}
            <CareLog
              plantId={plant.plant_id}
              plant={plant}
              careLog={careLog}
              isLocalMode={isLocalMode}
              onLogChange={setCareLog}
              onCoverPromoted={() => { onPlantAdded(); loadAllPhotos(); }}
            />

            {/* Date Added */}
            {plant.created_at && (
              <p className="text-xs pt-4" style={{ color: 'hsl(125, 12%, 55%)' }} data-testid="plant-date-added">
                {t('addedOn')} {(() => {
                  const d = new Date(plant.created_at);
                  return isNaN(d) ? '' : `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`;
                })()}
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-5 justify-end">
              {onShareThisPlant && (
                <button
                  onClick={() => onShareThisPlant(plant)}
                  className="p-2.5 rounded-full bg-white shadow-md hover:shadow-lg transition-all hover:scale-110"
                  style={{ border: '1px solid hsl(143, 28%, 85%)' }}
                  data-testid="detail-share-plant-button"
                  title="Share this plant"
                  aria-label="Share this plant"
                >
                  <Share2 className="w-5 h-5" style={{ color: 'hsl(143, 28%, 25%)' }} />
                </button>
              )}
              {onShowQr && !plant.incomplete && (
                <button
                  onClick={() => onShowQr(plant)}
                  className="p-2.5 rounded-full bg-white shadow-md hover:shadow-lg transition-all hover:scale-110"
                  style={{ border: '1px solid hsl(143, 28%, 85%)' }}
                  data-testid="detail-qr-plant-button"
                  title={t('showQrCode') || 'Show QR code'}
                  aria-label={t('showQrCode') || 'Show QR code'}
                >
                  <QrCode className="w-5 h-5" style={{ color: 'hsl(143, 28%, 25%)' }} />
                </button>
              )}
              <button
                onClick={() => onEdit(plant)}
                className="p-2.5 rounded-full bg-white shadow-md hover:shadow-lg transition-all hover:scale-110"
                style={{ border: '1px solid hsl(143, 28%, 85%)' }}
                data-testid="detail-edit-plant-button"
                title={t('editPlant')}
              >
                <Pencil className="w-5 h-5" style={{ color: 'hsl(143, 28%, 25%)' }} />
              </button>
              {!plant.archived && (
                <button
                  onClick={() => setShowArchiveDialog(true)}
                  className="p-2.5 rounded-full bg-white shadow-md hover:shadow-lg transition-all hover:scale-110"
                  style={{ border: '1px solid hsl(40, 70%, 85%)' }}
                  data-testid="archive-plant-button"
                  title="Archive plant"
                  aria-label="Archive plant"
                >
                  <Archive className="w-5 h-5" style={{ color: 'hsl(40, 60%, 40%)' }} />
                </button>
              )}
              <button
                onClick={() => setShowDeleteAlert(true)}
                className="p-2.5 rounded-full bg-white shadow-md hover:shadow-lg transition-all hover:scale-110"
                style={{ border: '1px solid hsl(0, 70%, 85%)' }}
                data-testid="delete-plant-button"
                title={t('delete')}
              >
                <Trash2 className="w-5 h-5" style={{ color: 'hsl(0, 70%, 50%)' }} />
              </button>
            </div>
          </div>

          {/* First-time peek hint: subtle bouncing chevron pill anchored to
              the bottom of the modal, nudging mobile users to scroll for
              care details. Tapping actually SCROLLS the content — earlier
              versions only dismissed the pill silently, which felt broken
              because the user tapped "Scroll for care details" and
              nothing appeared to happen. Auto-dismisses on scroll, close,
              or 6s (still). */}
          {showPeekHint && (
            <button
              type="button"
              onClick={() => {
                const el = scrollAreaRef.current;
                if (el) {
                  el.scrollBy({ top: Math.max(240, el.clientHeight * 0.6), behavior: 'smooth' });
                }
                dismissPeekHint();
              }}
              className="sm:hidden absolute bottom-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-lg text-xs font-medium animate-bounce"
              style={{
                backgroundColor: 'hsl(143, 45%, 34%)',
                color: 'white',
                border: '1px solid hsl(143, 45%, 28%)',
              }}
              aria-label={t('scrollForCareDetails') || 'Scroll for care details'}
              data-testid="modal-peek-hint"
            >
              <ChevronDown size={14} aria-hidden="true" />
              <span>{t('scrollForCareDetails') || 'Scroll for care details'}</span>
            </button>
          )}
        </DialogContent>
      </Dialog>

      {/* Enlarged Photo - Full Screen.
          Uses a Radix Dialog so it properly layers above the parent
          detail Dialog (Radix handles nested portal stacking and pointer
          events). Closing this only closes the enlarged view, not the
          parent card. */}
      <Dialog
        open={enlargedPhoto !== null && photos.length > 0}
        onOpenChange={(open) => { if (!open) setEnlargedPhoto(null); }}
      >
        <DialogContent
          hideCloseButton
          className="max-w-none w-screen h-screen p-0 bg-black/95 border-0 rounded-none flex items-center justify-center"
          data-testid="enlarged-photo-modal"
          aria-describedby={undefined}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{plant.common_name || t('unnamedPlant') || 'Unnamed plant'}</DialogTitle>
          </DialogHeader>
          <button
            onClick={() => setEnlargedPhoto(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-10"
            style={{ backgroundColor: 'rgba(255,255,255,0.92)', border: '1px solid rgba(0,0,0,0.08)' }}
            data-testid="enlarged-photo-close"
            aria-label="Close full screen photo"
          >
            <X className="w-5 h-5" style={{ color: 'hsl(143, 28%, 25%)' }} />
          </button>
          {/* Re-crop this photo — same dialog as Add/Replace, pre-loaded
              with the current image. Applies the result back to the same
              photo index via the existing replace path. Hidden while
              the crop dialog is open so we don't stack overlays. */}
          {enlargedPhoto !== null && !pendingPhotoFile && (
            <button
              onClick={async () => {
                const idx = enlargedPhoto;
                const cur = photos[idx];
                if (!cur) return;
                try {
                  let blob = null;
                  // Prefer the authoritative source over the possibly
                  // stale `src` URL:
                  //   • Drive mode → refetch by drive_id (in case the
                  //     original blob URL was revoked or GC'd on
                  //     mobile Chrome).
                  //   • Local mode → pull raw base64 from IndexedDB.
                  // Falls back to `fetch(src)` only if neither works.
                  if (isLocalMode) {
                    const list = await getAllLocalPhotos(plant.plant_id);
                    const p = list?.[idx];
                    const b64 = p?.image_base64 || p?.thumbnail_base64;
                    if (b64) {
                      const bin = atob(b64);
                      const bytes = new Uint8Array(bin.length);
                      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
                      blob = new Blob([bytes], { type: 'image/jpeg' });
                    }
                  } else if (cur.driveId) {
                    const sessionToken = localStorage.getItem('auth_hint');
                    const resp = await axios.get(`${BACKEND_URL}/api/photos/${cur.driveId}`, {
                      headers: { Authorization: `Bearer ${sessionToken}` },
                      responseType: 'blob',
                    });
                    blob = resp.data;
                  }
                  // Last-resort fallback — try fetching the display URL.
                  if (!blob && cur.src) {
                    const resp = await fetch(cur.src);
                    blob = await resp.blob();
                  }
                  if (!blob) throw new Error('no photo source');
                  const file = new File([blob], `plant-${idx}.jpg`, {
                    type: blob.type || 'image/jpeg',
                  });
                  replaceIndexRef.current = idx;
                  setPendingPhotoFile(file);
                  setPendingPhotoMode({ mode: 'replace', index: idx });
                  setEnlargedPhoto(null);
                } catch (err) {
                  console.error('Re-crop: failed to load photo', err);
                  toast.error(t('cropReopenFailed') || 'Could not open photo to re-crop');
                }
              }}
              className="absolute top-4 right-16 w-10 h-10 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-10"
              style={{ backgroundColor: 'rgba(255,255,255,0.92)', border: '1px solid rgba(0,0,0,0.08)' }}
              data-testid="enlarged-photo-recrop"
              aria-label={t('cropAndRotate') || 'Crop & rotate'}
              title={t('cropAndRotate') || 'Crop & rotate'}
            >
              <Crop className="w-5 h-5" style={{ color: 'hsl(143, 28%, 25%)' }} />
            </button>
          )}
          {enlargedPhoto !== null && (
            <div
              className="relative w-full h-full flex items-center justify-center overflow-hidden"
              style={{ touchAction: 'none' }}
              onTouchStart={(e) => {
                const g = gestureRef.current;
                if (e.touches.length === 2) {
                  const dx = e.touches[0].clientX - e.touches[1].clientX;
                  const dy = e.touches[0].clientY - e.touches[1].clientY;
                  g.mode = 'pinch';
                  g.startDist = Math.hypot(dx, dy);
                  g.startScale = zoom.scale;
                  g.moved = true;
                } else if (e.touches.length === 1) {
                  const now = Date.now();
                  if (now - g.lastTap < 300) { cancelClose(); toggleZoom(); g.mode = 'dbltap'; g.lastTap = 0; return; }
                  g.lastTap = now;
                  g.mode = zoom.scale > 1 ? 'pan' : 'swipe';
                  g.startX = e.touches[0].clientX;
                  g.startY = e.touches[0].clientY;
                  g.startTx = zoom.x;
                  g.startTy = zoom.y;
                  g.moved = false;
                  enlargedTouchRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, moved: false };
                }
              }}
              onTouchMove={(e) => {
                const g = gestureRef.current;
                if (g.mode === 'pinch' && e.touches.length === 2) {
                  const dx = e.touches[0].clientX - e.touches[1].clientX;
                  const dy = e.touches[0].clientY - e.touches[1].clientY;
                  const dist = Math.hypot(dx, dy);
                  const scale = Math.max(1, Math.min(MAX_ZOOM, g.startScale * (dist / (g.startDist || 1))));
                  setZoom(z => { const p = clampPan(scale, z.x, z.y); return { scale, x: p.x, y: p.y }; });
                } else if (g.mode === 'pan' && e.touches.length === 1) {
                  const nx = g.startTx + (e.touches[0].clientX - g.startX);
                  const ny = g.startTy + (e.touches[0].clientY - g.startY);
                  const p = clampPan(zoom.scale, nx, ny);
                  setZoom(z => ({ ...z, x: p.x, y: p.y }));
                  g.moved = true;
                } else if (g.mode === 'swipe') {
                  const dx = e.touches[0].clientX - g.startX;
                  const dy = e.touches[0].clientY - g.startY;
                  if (Math.abs(dx) > 10 || Math.abs(dy) > 10) { g.moved = true; enlargedTouchRef.current.moved = true; }
                }
              }}
              onTouchEnd={(e) => {
                const g = gestureRef.current;
                if (g.mode === 'swipe' && zoom.scale === 1 && photos.length > 1) {
                  const dx = e.changedTouches[0].clientX - g.startX;
                  const dy = e.changedTouches[0].clientY - g.startY;
                  if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
                    if (dx < 0) showNextEnlarged(); else showPrevEnlarged();
                  }
                }
                if (g.mode === 'pinch' && zoom.scale < 1.1) resetZoom();
                g.mode = null;
              }}
              onWheel={(e) => {
                const scale = Math.max(1, Math.min(MAX_ZOOM, zoom.scale - e.deltaY * 0.002));
                setZoom(z => { const p = clampPan(scale, z.x, z.y); return { scale, x: p.x, y: p.y }; });
              }}
              onDoubleClick={(e) => { e.stopPropagation(); cancelClose(); toggleZoom(); }}
              onMouseDown={(e) => {
                if (zoom.scale <= 1) return;
                const g = gestureRef.current;
                g.pointerDown = true; g.startX = e.clientX; g.startY = e.clientY; g.startTx = zoom.x; g.startTy = zoom.y;
              }}
              onMouseMove={(e) => {
                const g = gestureRef.current;
                if (!g.pointerDown || zoom.scale <= 1) return;
                const p = clampPan(zoom.scale, g.startTx + (e.clientX - g.startX), g.startTy + (e.clientY - g.startY));
                setZoom(z => ({ ...z, x: p.x, y: p.y }));
              }}
              onMouseUp={() => { gestureRef.current.pointerDown = false; }}
              onMouseLeave={() => { gestureRef.current.pointerDown = false; }}
            >
              <img
                src={photos[enlargedPhoto]?.src || photos[0]?.src}
                alt={photos.length > 1 ? `${plant.common_name} — photo ${enlargedPhoto + 1} of ${photos.length}` : plant.common_name}
                className="max-w-full max-h-[90vh] object-contain select-none"
                draggable={false}
                style={{
                  transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
                  transition: gestureRef.current.mode ? 'none' : 'transform 0.2s ease-out',
                  cursor: zoom.scale > 1 ? 'grab' : 'zoom-in',
                }}
                data-testid="enlarged-photo-img"
                onClick={() => { if (zoom.scale === 1 && !enlargedTouchRef.current.moved) scheduleClose(); }}
              />

              {photos.length > 1 && zoom.scale === 1 && (
                <>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); showPrevEnlarged(); }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/70 transition-colors"
                    style={{ touchAction: 'manipulation' }}
                    aria-label="Previous photo"
                    data-testid="enlarged-prev-btn"
                  >
                    <ChevronLeft size={26} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); showNextEnlarged(); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/70 transition-colors"
                    style={{ touchAction: 'manipulation' }}
                    aria-label="Next photo"
                    data-testid="enlarged-next-btn"
                  >
                    <ChevronRight size={26} aria-hidden="true" />
                  </button>
                </>
              )}
              {photos.length > 1 && (
                <div
                  className="absolute bottom-6 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/60 text-white text-sm font-medium z-20"
                  aria-live="polite" aria-atomic="true"
                  data-testid="enlarged-counter"
                >
                  {enlargedPhoto + 1} / {photos.length}
                </div>
              )}
              {zoom.scale > 1 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); resetZoom(); }}
                  className="absolute bottom-6 right-4 z-20 px-3 py-1.5 rounded-full bg-black/60 text-white text-xs font-medium hover:bg-black/75 transition-colors"
                  data-testid="enlarged-reset-zoom"
                >
                  {t('resetZoom') || 'Reset zoom'}
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent style={{ backgroundColor: 'white' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: 'hsl(143, 28%, 25%)', fontSize: '1.5rem' }}>{t('deleteThisPlant')}</AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'hsl(125, 12%, 25%)', fontSize: '1rem' }}>
              {isLocalMode ? t('deleteConfirmLocal') : t('deleteConfirmDrive')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} style={{ backgroundColor: 'hsl(0, 70%, 50%)', color: 'white' }}>
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Dialog */}
      <ArchiveDialog
        isOpen={showArchiveDialog}
        plantName={plant.common_name}
        onConfirm={handleArchive}
        onCancel={() => setShowArchiveDialog(false)}
      />

      {/* Photo Source Chooser — appears after the user clicks "Add photo" */}
      <Dialog open={sourcePickerFor !== null} onOpenChange={(open) => { if (!open) setSourcePickerFor(null); }}>
        <DialogContent className="max-w-sm" data-testid="photo-source-picker" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle style={{ color: 'hsl(143, 28%, 25%)' }}>{t('addPhoto') || 'Add photo'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              onClick={() => handleSourceChoice('camera')}
              className="border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer hover:border-primary transition-colors"
              style={{ borderColor: 'hsl(143, 28%, 70%)', backgroundColor: 'hsl(82, 55%, 98%)' }}
              data-testid="photo-source-camera"
            >
              <Camera className="w-10 h-10 mx-auto mb-2" style={{ color: 'hsl(143, 28%, 25%)', opacity: 0.6 }} />
              <p className="text-sm font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>{t('fromCamera') || 'From Camera'}</p>
            </button>
            <button
              onClick={() => handleSourceChoice('gallery')}
              className="border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer hover:border-primary transition-colors"
              style={{ borderColor: 'hsl(143, 28%, 70%)', backgroundColor: 'hsl(82, 55%, 98%)' }}
              data-testid="photo-source-gallery"
            >
              <ImagePlus className="w-10 h-10 mx-auto mb-2" style={{ color: 'hsl(143, 28%, 25%)', opacity: 0.6 }} />
              <p className="text-sm font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>{t('fromGallery') || 'From Gallery'}</p>
            </button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Crop & rotate before every add / replace so photos always
          land right-side up, matching the initial identification's
          crop UX. */}
      <PhotoCropDialog
        open={!!pendingPhotoFile}
        sourceFile={pendingPhotoFile}
        title={pendingPhotoMode?.mode === 'add' && addPhotoTotal > 1
          ? (t('bulkPhotoProgress') || 'Add photo {n} of {total}')
              .replace('{n}', addPhotoTotal - addPhotoQueue.length)
              .replace('{total}', addPhotoTotal)
          : undefined}
        onCancel={() => {
          setPendingPhotoFile(null);
          setPendingPhotoMode(null);
          setAddPhotoQueue([]);
          setAddPhotoTotal(0);
          if (photoInputRef.current) photoInputRef.current.value = '';
          if (photoCameraInputRef.current) photoCameraInputRef.current.value = '';
          if (replaceInputRef.current) replaceInputRef.current.value = '';
          if (replaceCameraInputRef.current) replaceCameraInputRef.current.value = '';
        }}
        onDone={(base64, dataUrl) => {
          const mode = pendingPhotoMode;
          setPendingPhotoFile(null);
          setPendingPhotoMode(null);
          if (mode?.mode === 'replace' && typeof mode.index === 'number') {
            doReplacePhotoUpload(base64, dataUrl, mode.index);
          } else {
            doAddPhotoUpload(base64);
          }
        }}
      />
    </>
  );
};
